import { useEffect, useState } from 'react'
import { Zap } from 'lucide-react'
import { useLiveStore } from '../../../stores/live'
import { triggerStrengthPercent } from '../../../lib/seismic'
import { cn } from '../../../lib/utils'

const ago = (ts: number) => {
  const s = Math.max(0, Math.floor((Date.now() - ts) / 1000))
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m${s % 60}s`
}

/**
 * 实时触发流（第三层降级版）：Android 端不上报原始波形，
 * 以 trigger 推送的特征值（CAV 强度条）呈现 GlobalQuake 式站点实时观感。
 * 真波形通道视图待端上波形上报链路落地后接入。
 */
export function LiveStationsPanel() {
  const triggers = useLiveStore((s) => s.triggers)
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [])

  const perMin = triggers.filter((t) => now - t.ts <= 60_000).length

  // 每个设备取最近一条，按时间倒序
  const latest = new Map<string, typeof triggers[number]>()
  for (const t of triggers) if (!latest.has(t.deviceId)) latest.set(t.deviceId, t)
  const stations = Array.from(latest.values()).sort((a, b) => b.ts - a.ts).slice(0, 10)

  return (
    <div className="panel-glass flex min-h-0 flex-col rounded-xl">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h2 className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
          <Zap size={13} className="text-primary" /> 实时触发流
        </h2>
        <span className="font-mono text-[10px] text-muted-foreground">{perMin} 次/分 · MQTT 直推</span>
      </div>
      {stations.length === 0 ? (
        <div className="flex min-h-[120px] flex-1 flex-col items-center justify-center gap-1 px-4 text-center">
          <span className="text-xs text-muted-foreground">链路暂无触发</span>
          <span className="text-[10px] text-muted-foreground/60">终端检测到震动后将实时推流至此</span>
        </div>
      ) : (
        <div className="divide-y divide-border">
          {stations.map((t) => {
            const strength = triggerStrengthPercent(t.cav)
            const fresh = now - t.ts <= 5_000
            return (
              <div key={t.deviceId} className="flex items-center gap-3 px-4 py-2">
                <span
                  className={cn(
                    'size-1.5 shrink-0 rounded-full',
                    fresh ? 'bg-primary shadow-[0_0_6px_rgba(86,215,255,.8)]' : 'bg-muted-foreground/40',
                  )}
                />
                <span className="w-24 shrink-0 truncate font-mono text-[11px] text-foreground">
                  {t.deviceId.slice(-8)}
                </span>
                <div className="relative h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-foreground/[.06]">
                  <div
                    className={cn('absolute inset-y-0 left-0 rounded-full', t.confirmed ? 'bg-critical' : 'bg-primary')}
                    style={{ width: `${Math.max(4, strength)}%` }}
                  />
                </div>
                <span className="w-14 shrink-0 text-right font-mono text-[10px] text-muted-foreground">
                  CAV {Math.round(t.cav)}
                </span>
                <span className="w-10 shrink-0 text-right font-mono text-[10px] text-muted-foreground/70">
                  {ago(t.ts)}
                </span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
